import { inject, Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, tap } from 'rxjs';

import { toetsenInitialState, ToetsenStateModel } from '@store/models/toetsen.model';
import { ApiClientService } from '../../core/services/api-client.service';
import { APP_CONFIG, AppConfig } from '../../core/config';
import { LoadToetsen } from '@store/actions/toetsen.actions';
import { PeriodItemI, PeriodItemITypeEnum, PeriodsI } from '@models/periods';
import { Toets } from '@models/toetsen';

@State<ToetsenStateModel>({
  name: 'toetsen',
  defaults: toetsenInitialState,
})
@Injectable()
export class ToetsenState {
  private readonly apiClientService = inject(ApiClientService);
  private readonly dstConfig: AppConfig = inject(APP_CONFIG);

  @Selector()
  static toetsen(state: ToetsenStateModel): Toets[] | null {
    return state.toetsen;
  }

  @Action(LoadToetsen)
  loadToetsen(ctx: StateContext<ToetsenStateModel>) {
    const state = ctx.getState();
    ctx.patchState({ ...state, isLoading: true, isLoaded: false, error: null });

    return this.apiClientService.getToetsen(this.dstConfig.DST.ProductBusinessKey).pipe(
      tap((toetsen) => {
        // Map toetsen with businessKeyNoCampaign (preserve existing logic)
        const mappedToetsen = toetsen.map((toets) => ({
          ...toets,
          businessKeyNoCampaign: toets.businessKey.replace(/-\d{1,2}$/, ''),
        }));

        // Calculate periods (skip for LIBDST)
        let datumInplanbaarVanaf = '';
        let datumInplanbaarTot = '';
        let datumAfnameVanaf = '';
        let datumAfnameTot = '';

        if (this.dstConfig.DST.ProductBusinessKey !== 'LIBDST') {
          // Find min and max dates for inplanbaar and afname in toetsen
          datumInplanbaarVanaf = mappedToetsen
            .filter((toets: Toets) => {
              return toets.datumInplanbaarVanaf != null;
            })
            .reduce((prev: Toets, curr: Toets) => {
              return Date.parse(prev.datumInplanbaarVanaf) < Date.parse(curr.datumInplanbaarVanaf) ? prev : curr;
            }).datumInplanbaarVanaf;

          datumInplanbaarTot = mappedToetsen
            .filter((toets: Toets) => {
              return toets.datumInplanbaarTot != null;
            })
            .reduce((prev: Toets, curr: Toets) => {
              return Date.parse(prev.datumInplanbaarTot) > Date.parse(curr.datumInplanbaarTot) ? prev : curr;
            }).datumInplanbaarTot;

          datumAfnameVanaf = mappedToetsen
            .filter((toets: Toets) => {
              return toets.datumAfnameVanaf != null;
            })
            .reduce((prev: Toets, curr: Toets) => {
              return Date.parse(prev.datumAfnameVanaf) < Date.parse(curr.datumAfnameVanaf) ? prev : curr;
            }).datumAfnameVanaf;

          datumAfnameTot = mappedToetsen
            .filter((toets: Toets) => {
              return toets.datumAfnameTot != null;
            })
            .reduce((prev: Toets, curr: Toets) => {
              return Date.parse(prev.datumAfnameTot) > Date.parse(curr.datumAfnameTot) ? prev : curr;
            }).datumAfnameTot;
        }

        // Create period objects for inplanbaar and afname
        const periods = {} as PeriodsI;
        periods['datumInplanbaar'] = this.datesToPeriod([datumInplanbaarVanaf, datumInplanbaarTot]);
        periods['datumAfname'] = this.datesToPeriod([datumAfnameVanaf, datumAfnameTot]);
        periods['datumInplanbaarTotEnMetAfname'] = this.datesToPeriod([datumInplanbaarVanaf, datumAfnameTot]);

        ctx.patchState({
          isLoading: false,
          toetsen: mappedToetsen,
          periods,
          isLoaded: true,
        });
      }),
      catchError((error) => {
        ctx.patchState({ isLoading: false, error: error.message });
        throw error;
      }),
    );
  }

  /**
   * Normalizes a date string by adding 'Z' if it does not contain 'Z' and does not end with '+00:00'.
   * @param dateStr The date string to normalize.
   * @returns The normalized date string or null if the input is null.
   */
  private ensureTimezone(dateStr: string | null): string | null {
    if (!dateStr) {
      return null;
    }

    const timezoneDesignatorRegex = /\+\d{2}:\d{2}$/; // example +00:00
    const hasTimezoneDesignator = dateStr.includes('Z') || timezoneDesignatorRegex.test(dateStr);

    if (!hasTimezoneDesignator) {
      return dateStr + 'Z';
    }

    return dateStr;
  }

  /**
   * Compares two dates (strings) and create a period object
   * @param dateStrings
   */
  datesToPeriod(dateStrings: (string | null)[]): PeriodItemI {
    // HK - If the backend does not send a Z or +00:00 timezone designator, add it.
    //      Otherwise, the date will be interpreted as local time
    const fromDateStr = this.ensureTimezone(dateStrings[0]);
    const toDateStr = this.ensureTimezone(dateStrings[1]);

    const currentUtcDate = new Date();

    let withinRange = false;
    let type = PeriodItemITypeEnum.NONE;

    if (fromDateStr && toDateStr) {
      // Both start and end date set, so check if current date is within range
      withinRange = currentUtcDate >= new Date(fromDateStr) && currentUtcDate <= new Date(toDateStr);
      type = PeriodItemITypeEnum.RANGE;
    } else if (fromDateStr && !toDateStr) {
      // No end date set, so greater or equal to start date is within range
      withinRange = currentUtcDate >= new Date(fromDateStr);
      type = PeriodItemITypeEnum.FROM;
    } else if (!fromDateStr && toDateStr) {
      // No start date set, so less or equal to end date is within range
      withinRange = currentUtcDate <= new Date(toDateStr);
      type = PeriodItemITypeEnum.TO;
    } else {
      // No dates set, so always within range
      withinRange = true;
      type = PeriodItemITypeEnum.NONE;
    }

    return {
      withinRange: withinRange,
      startDate: fromDateStr,
      endDate: toDateStr || null,
      currentUtcDate: currentUtcDate.toISOString(),
      type: type,
    };
  }
}
